/**
 * The tab's Zitadel client and the callback it found at startup (OWA-1137). Used only when
 * `IDP_CONFIG.provider` is 'zitadel'; importing it in Auth0 mode creates nothing.
 *
 * @module client/Auth/zitadelSession
 */

import IDP_CONFIG from './idp-config';
import {tabSession} from './session';
import {firmChoices, ZitadelClient, type CallbackResult, type FirmChoice} from './zitadelClient';

let client: ZitadelClient | undefined;
let startup: Promise<CallbackResult> | undefined;
let startupChecked = false;

export function getZitadelClient(): ZitadelClient {
    if (!client) {
        client = new ZitadelClient({
            issuer: `https://${IDP_CONFIG.domain}`,
            clientId: IDP_CONFIG.clientId,
            projectId: IDP_CONFIG.audience,
            redirectUri: IDP_CONFIG.callbackUrl,
        // This tab's own storage, under the same namespace as its session.
        }, window.sessionStorage, tabSession.namespace);
        const active = client;
        tabSession.setRenewal(() => active.renew());
    }
    return client;
}

/** The exchange of this tab's own callback, started once when the page loads on it. */
export function startupCallback(): Promise<CallbackResult> | undefined {
    if (!startupChecked) {
        startupChecked = true;
        const url = new URL(window.location.href);
        const zitadel = getZitadelClient();
        if (zitadel.isCallback(url)) startup = zitadel.handleCallback(url);
    }
    return startup;
}

/** Send this tab to Zitadel (sign-in, firm choice or sign-out). */
export function navigate(url: string): void {
    window.location.assign(url);
}

/** The firms a callback's ID token names. More than one means the person must choose before the
 * tab authorizes again with that firm's roles scope. */
export function callbackFirms(result: CallbackResult): FirmChoice[] {
    const payload = result.credentials.idToken.split('.')[1] ?? '';
    try {
        const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return firmChoices(claims, IDP_CONFIG.audience);
    } catch {
        return [];
    }
}
